<template>
  <section>
    <one-line-form :submit="updatePhone" :submittable="isValid" :saving="isSaving ? phone : ''">
      <input-field name="phone"
                   :class="{ invisible: isSaving }"
                   :title="i18n.t('phone:mobile_number')"
                   :placeholder="i18n.t('phone:enter_mobile_number')"
                   :value="phone"
                   v-mask="'ppppppppppppppppppppppppppppppppppppppp'"
                   @maskInput="syncPhone"
                   v-keyboard="'tel'"
                   type="'tel'"
                   ref='input'
                   :autocomplete="'tel'" />
    </one-line-form>
    <helper v-if="!isSaving && phonePatternExample && !showWarning && showElements" x-ms-format-detection="none">
      {{ i18n.t('base:example') }}: {{ phonePatternExample }}
    </helper>
    <numb-area></numb-area>
    <show-keyboard></show-keyboard>
  </section>
</template>

<style lang="scss" rel="stylesheet/scss" scoped>
  @import '../assets/mixins';

ul {
    @include button-list;
    li{
      text-align: center;
      padding: 0;
    }
  }
  ins {
      color: transparent;
      background: #FFE676;
      display: inline-block;
      position: relative;
      span {
        background: no-repeat left 9px;
        background-image: url('~static/warning_pointer.svg');
        position: absolute;
        display: block;
        left: 40px;
        bottom: 100%;
        height: 40px;
        color: $inputWarningColor;
        color: $inputWarningColor-portal;
        font-size: 1rem;
        font-weight: lighter;
        padding-left: 42px;
        margin-bottom: 10px;
        line-height: 120%;
      }
    }

    .warning-info{
      position: absolute;
      bottom: 100px;
      width: 100%;
      left: 0;

      @include desktop {
        bottom: 80px;
      }
    }

    .actions{
      z-index: 500;
    }

    .warning-info{
      font-family: $mainFontFamily;
      font-family: $mainFontFamily-portal;
      line-height: 25px;

      @media (max-width: #{$small-phone-width}) {
        line-height: 21px;
        bottom: 50px;
      }

      @include desktop-big {
        bottom: 275px;
        h3 {
          display: none;
        }
        p {
          line-height: 18px;
        }
      }

      h3{
        font-family: $hintFontFamily;
        font-family: $hintFontFamily-portal;
        color: $inputRegularHeaderColor;
        color: $inputRegularHeaderColor-portal;
        font-size: $inputRegularHeaderSize;
        font-size: $inputRegularHeaderSize-portal;
        font-weight: $inputRegularHeaderWeight;
        font-weight: $inputRegularHeaderWeight-portal;
      }
      p{
        font-family: $hintFontFamily;
        font-family: $hintFontFamily-portal;
        color: $inputRegularBodyColor;
        color: $inputRegularBodyColor-portal;
        font-size: $inputRegularBodySize;
        font-size: $inputRegularBodySize-portal;
        font-weight: $inputRegularBodyWeight;
        font-weight: $inputRegularBodyWeight-portal;
      }
    }
</style>

<script>
  import Vue from 'vue'
  import { mapState, mapMutations } from 'vuex'
  import Screen from '../components/Screen'
  import { AsYouType } from 'libphonenumber-js'

  const libphonenumber = () => import('../utils/libphonenumberProxy')
  export default Vue.component('phone-screen', {
    components: {
      'screen': Screen
    },
    props: ['screenParams'],
    data () {
      const user = this.$store.state.user
      const app = this.$store.state.app
      const autofill = this.$store.state.autofill

      return {
        phone: new AsYouType(app.country.code).input(user.phone || (autofill.enabled && autofill.phone) || ''),
        phoneOld: '',
        maskedPhone: '',
        phonePatternExample: '',
        showWarning: false,
        showElements: false,
        isValid: false,
        ready: false,
        isSaving: false,
        fromVerificationCode: this.$store.state.route.from.fullPath.includes('sms-code')
      }
    },
    watch: {
      'app.country.phonePattern' () {
        this.updatePhonePatternExample()
      },
      showWarning () {
        if (this.showWarning && document.activeElement.nodeName.toLowerCase() === 'input') {
          document.activeElement.blur()
        }

        this.screenType = this.showWarning ? 'error' : ''
      },
      phone (newVal, oldVal) {
        if (newVal.length < oldVal.length) {
          if (/\D/.test(newVal.slice(-1))) this.phone = newVal.slice(0, -1)
        } else {
          setTimeout(() => {
            this.phone = new AsYouType(this.app.country.code).input(newVal)
          }, 0)
        }

        this.validatePhone()
      }
    },
    computed: {
      ...mapState(['app', 'user', 'route'])
    },
    methods: {
      ...mapMutations(['updatePhoneProcessing']),
      pickCountryPrefix (value, data, index) {
        this.showPhoneCountrySelector = false
        this.updatePhoneProcessing({
          isValidationEnabled: false,
          countryCode: data.countryCode
        })
        this.validatePhone().then(() => {
          this.updatePhone()
        })
      },
      syncPhone (value, maskedValue) {
        if (this.usingPrefill && value !== this.phone) this.usingPrefill = false
        this.phone = value
        this.maskedPhone = maskedValue
      },
      updatePhonePatternExample () {
        return new Promise((resolve, reject) => {
          if (!this.app.country || !this.app.country.phonePattern) { this.phonePatternExample = ''; return resolve() }

          libphonenumber().then(libphonenumberMethods => {
            const parsedPhonePattern = libphonenumberMethods.parse(this.app.country.phonePattern, this.app.country.code)
            const internationalPhonePattern = libphonenumberMethods.format(parsedPhonePattern, 'E.164')
            if (this.app.country.code === 'JP') {
              this.phonePatternExample = `${this.app.country.phonePattern}`
            } else {
              this.phonePatternExample = `${this.app.country.phonePattern} / ${internationalPhonePattern}`
            }
            return resolve()
          })
        })
      },
      updatePhone () {
        const rawNumber = this.phone.replace(/\D/g, '')

        libphonenumber().then(libphonenumberMethods => {
          const examplePhoneNumber = this.phonePatternExample
          this.preparePhone(rawNumber, this.user.country || this.app.country.code).then(phone => {
            let extendedValidation = false
            if (phone.country && phone.phone) {
              extendedValidation = examplePhoneNumber ? libphonenumberMethods.isValidNumber(phone) && libphonenumberMethods.getNumberType(phone) !== undefined : Object.keys(phone).length > 0
            }
            this.isSaving = true
            this.$store.dispatch('updateUser', {
              phone: extendedValidation ? libphonenumberMethods.format(phone, 'National').replace(/\D/g, '') : rawNumber
            }).then(() => {
              this.trackPrefill('phone', rawNumber)
              this.$store.dispatch('updatePhonePrevalidation')
            })
            if (this.fromVerificationCode) {
              return this.$store.dispatch('replace', this.route.from.fullPath)
            }
            return this.$store.dispatch('next', this.screenParams.nextScreenRoute)
          })
        })
      },
      editPhone () {
        if (this.ready) {
          this.isSaving = false
          this.showWarning = false

          const input = this.$el.querySelector('input')

          if (input) {
            input.focus()
          }
        }
      },
      preparePhone (phone, countryCode) {
        return libphonenumber().then(libphonenumberMethods => {
          let preParsePhone = (phone && phone.replace(/^00/, '+')) || ''
          const cleanParse = libphonenumberMethods.parse(preParsePhone, countryCode)
          if (!cleanParse.phone || !libphonenumberMethods.getNumberType(cleanParse)) {
            const phoneCode = countryCode && libphonenumberMethods.getPhoneCode(countryCode)
            const hasCountryCodeNoPlus = preParsePhone && !preParsePhone.includes('+') && preParsePhone.substring(0, 2) === phoneCode
            const plusParse = libphonenumberMethods.parse(`+${preParsePhone}`, countryCode)
            return hasCountryCodeNoPlus ? plusParse : cleanParse
          }
          return cleanParse
        })
      },
      validatePhone () {
        const countryCode = this.user.country
        const examplePhoneNumber = this.phonePatternExample
        const phoneNumber = this.phone
        let numbers = phoneNumber && phoneNumber.match(/\d/g)

        return new Promise((resolve, reject) => {
          if (this.validate && countryCode) {
            this.preparePhone(this.phone, countryCode).then(phone => {
              if (!phone.phone || !phone.country) {
                this.isValid = false
                resolve()
              } else if (this.validate && countryCode && examplePhoneNumber) {
                return libphonenumber().then(libphonenumberMethods => {
                  this.isValid = (libphonenumberMethods.isValidNumber(phone) && libphonenumberMethods.getNumberType(phone) !== undefined) || (this.phone.match(/^(\+|00)/g) && this.phone.length > 10)
                  resolve()
                })
              } else if (countryCode && !examplePhoneNumber) {
                this.isValid = Object.keys(phone).length > 0 || false
                resolve()
              }
            })
          } else {
            this.isValid = !!numbers && numbers.length >= 5
            resolve()
          }
        })
      },
      async initStateProcessing () {
        await this.updatePhonePatternExample()
        await this.validatePhone()

        if (!!this.user.phone && !this.isValid) {
          this.showWarning = true
        }
      }
    },
    beforeCreate () {
      libphonenumber()
    },
    created () {
      this.initStateProcessing()
    },
    mounted () {
      setTimeout(() => {
        this.showElements = true
        this.ready = true
        if (this.showWarning && document.activeElement.nodeName.toLowerCase() === 'input') {
          document.activeElement.blur()
        }
      }, 0)
      this.phoneOld = this.phone
    }
  })
</script>



// WEBPACK FOOTER //
// src/screens/Phone.vue